import React, { useMemo, useState } from "react";
import InputMask from "react-input-mask";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { AlertTriangle, Activity } from "lucide-react";
import {
  DosageCalculatorProps,
  CalculatorResult,
} from "@/lib/dosageCalculators/types";

type Scale = "1" | "2";

interface ParamScore {
  raw: number | null;
  score: number; // -1 = not entered
}

function inRange(n: number, min: number, max: number): boolean {
  return !Number.isNaN(n) && n >= min && n <= max;
}

function scoreRespiration(value: string): number {
  if (value === "") return -1;
  const n = Number(value);
  if (!inRange(n, 1, 80)) return -1;
  if (n <= 8) return 3;
  if (n >= 9 && n <= 11) return 1;
  if (n >= 12 && n <= 20) return 0;
  if (n >= 21 && n <= 24) return 2;
  return 3;
}

function scoreSpo2(value: string, scale: Scale): number {
  if (value === "") return -1;
  const n = Number(value);
  if (!inRange(n, 50, 100)) return -1;
  if (scale === "1") {
    if (n <= 91) return 3;
    if (n <= 93) return 2;
    if (n <= 95) return 1;
    return 0;
  }
  // Scale 2 — for confirmed hypercapnic respiratory failure
  if (n <= 83) return 3;
  if (n <= 85) return 2;
  if (n <= 87) return 1;
  if (n <= 92) return 0;
  // Scale 2 above target on supplemental O2
  if (n <= 94) return 1;
  if (n <= 96) return 2;
  return 3;
}

function scoreSystolic(value: string): number {
  if (value === "") return -1;
  const n = Number(value);
  if (!inRange(n, 40, 300)) return -1;
  if (n <= 90) return 3;
  if (n <= 100) return 2;
  if (n <= 110) return 1;
  if (n <= 219) return 0;
  return 3;
}

function scorePulse(value: string): number {
  if (value === "") return -1;
  const n = Number(value);
  if (!inRange(n, 20, 250)) return -1;
  if (n <= 40) return 3;
  if (n <= 50) return 1;
  if (n <= 90) return 0;
  if (n <= 110) return 1;
  if (n <= 130) return 2;
  return 3;
}

function scoreTemperature(value: string): number {
  if (value === "") return -1;
  const n = Number(value);
  if (!inRange(n, 25, 45)) return -1;
  if (n <= 35.0) return 3;
  if (n <= 36.0) return 1;
  if (n <= 38.0) return 0;
  if (n <= 39.0) return 1;
  return 2;
}

function scoreConsciousness(value: string): number {
  if (value === "") return -1;
  return value === "alert" ? 0 : 3; // CVPU = 3
}

function hasCompleteBloodPressure(value: string): boolean {
  return /^\d{2,3}\/\d{2,3}$/.test(value.trim());
}

export function NEWS2Calculator({
  onComplete,
  onCancel,
  isDarkMode = false,
  isSaving = false,
}: DosageCalculatorProps & { isSaving?: boolean }) {
  const [scale, setScale] = useState<Scale>("1");
  const [respiration, setRespiration] = useState("");
  const [spo2, setSpo2] = useState("");
  const [supplementalOxygen, setSupplementalOxygen] = useState<
    "air" | "oxygen"
  >("air");
  const [bloodPressure, setBloodPressure] = useState(""); // "120/80" format
  const [bloodPressureError, setBloodPressureError] = useState("");
  const parseSystolic = (bp: string) => bp.split("/")[0]?.trim() || "";
  const [pulse, setPulse] = useState("");
  const [consciousness, setConsciousness] = useState("");
  const [temperature, setTemperature] = useState("");

  const respirationScore = scoreRespiration(respiration);
  const spo2Score = scoreSpo2(spo2, scale);
  const supplementalOxygenScore =
    spo2 === "" ? -1 : supplementalOxygen === "oxygen" ? 2 : 0;
  const systolicScore = scoreSystolic(parseSystolic(bloodPressure));
  const pulseScore = scorePulse(pulse);
  const consciousnessScore = scoreConsciousness(consciousness);
  const temperatureScore = scoreTemperature(temperature);

  const paramRows = [
    {
      key: "respiration",
      label: "Respiration rate",
      score: respirationScore,
      value: respiration,
      unit: "bpm",
    },
    {
      key: "spo2",
      label: `SpO₂ (Scale ${scale})`,
      score: spo2Score,
      value: spo2,
      unit: "%",
    },
    {
      key: "supplementalOxygen",
      label: "Supplemental oxygen",
      score: supplementalOxygenScore,
      value: supplementalOxygen === "oxygen" ? "On O₂" : "Air",
      unit: "",
    },
    {
      key: "bp",
      label: "Systolic BP (scored)",
      score: systolicScore,
      value: bloodPressure,
      unit: "mmHg",
    },

    {
      key: "pulse",
      label: "Pulse",
      score: pulseScore,
      value: pulse,
      unit: "bpm",
    },
    {
      key: "consciousness",
      label: "Consciousness",
      score: consciousnessScore,
      value: consciousness,
      unit: "",
    },
    {
      key: "temperature",
      label: "Temperature",
      score: temperatureScore,
      value: temperature,
      unit: "°C",
    },
  ];

  // Excludes supplementalOxygen from "scored params count" — it's implicit
  const corePerParam = [
    respirationScore,
    spo2Score,
    systolicScore,
    pulseScore,
    consciousnessScore,
    temperatureScore,
  ];
  const parametersScored = corePerParam.filter((s) => s >= 0).length;
  const parametersTotal = 6;

  const totalScore = useMemo(() => {
    return paramRows
      .map((r) => r.score)
      .filter((s) => s >= 0)
      .reduce((sum, s) => sum + s, 0);
  }, [
    respirationScore,
    spo2Score,
    supplementalOxygenScore,
    systolicScore,
    pulseScore,
    consciousnessScore,
    temperatureScore,
  ]);

  const hasSingleParam3 = corePerParam.some((s) => s === 3);

  const risk = (() => {
    if (parametersScored === 0) {
      return null;
    }
    if (totalScore >= 7) {
      return {
        level: "emergency" as const,
        label: "High risk — Emergency",
        description:
          "Consider critical care. Call 999 / direct to A&E immediately.",
        cls: isDarkMode
          ? "bg-red-900/30 border-red-700 text-red-200"
          : "bg-red-100 border-red-300 text-red-900",
      };
    }
    if (hasSingleParam3) {
      return {
        level: "single_param_3" as const,
        label: "Urgent — single parameter score 3",
        description:
          "One parameter scores 3 — escalate immediately regardless of total.",
        cls: isDarkMode
          ? "bg-orange-900/30 border-orange-700 text-orange-200"
          : "bg-orange-100 border-orange-300 text-orange-900",
      };
    }
    if (totalScore >= 5) {
      return {
        level: "urgent" as const,
        label: "Medium risk — Urgent response",
        description: "Urgent clinical review required.",
        cls: isDarkMode
          ? "bg-amber-900/30 border-amber-700 text-amber-200"
          : "bg-amber-100 border-amber-300 text-amber-900",
      };
    }
    return {
      level: "low" as const,
      label: "Low risk — Routine monitoring",
      description: "Continue routine monitoring per protocol.",
      cls: isDarkMode
        ? "bg-green-900/30 border-green-700 text-green-200"
        : "bg-green-100 border-green-300 text-green-900",
    };
  })();

  const limitedObs = parametersScored > 0 && parametersScored < 3;

  const handleSave = () => {
    if (bloodPressure.trim() !== "" && !hasCompleteBloodPressure(bloodPressure)) {
      setBloodPressureError("Enter blood pressure in full format, e.g. 120/80.");
      return;
    }

    setBloodPressureError("");
    const ts = new Date().toISOString();

    const individualScores = paramRows
      .filter((r) => r.score >= 0)
      .map((r) => ({
        parameter: r.label,
        value: String(r.value),
        score: r.score,
      }));

    const unavailable = paramRows
      .filter((r) => r.score < 0)
      .map((r) => r.label);

    // Build vitals entries so the host can save them as patient vitals.
    // Only include parameters that scored (i.e. were in valid clinical range).
    const vitals: CalculatorResult["vitals"] = [];
    if (respirationScore >= 0)
      vitals.push({
        type: "Respiration rate",
        value: respiration,
        unit: "bpm",
      });
    if (spo2Score >= 0) {
      vitals.push({ type: `SpO2 (Scale ${scale})`, value: spo2, unit: "%" });
      vitals.push({
        type: "Supplemental oxygen",
        value: supplementalOxygen === "oxygen" ? "On O2" : "Air",
      });
    }
    if (systolicScore >= 0)
      vitals.push({ type: "Systolic BP", value: bloodPressure, unit: "mmHg" });
    if (pulseScore >= 0)
      vitals.push({ type: "Pulse", value: pulse, unit: "bpm" });
    if (consciousnessScore >= 0)
      vitals.push({ type: "Consciousness (ACVPU)", value: consciousness });
    if (temperatureScore >= 0)
      vitals.push({ type: "Temperature", value: temperature, unit: "°C" });
    vitals.push({ type: "NEWS2 score", value: String(totalScore) });

    const notesLines = [
      `NEWS2 score: ${totalScore} (from ${parametersScored} of ${parametersTotal} parameters)`,
      `SpO₂ scale: ${scale}`,
      risk ? `Risk band: ${risk.label}` : null,
      hasSingleParam3 ? "Single parameter scored 3 — escalate" : null,
      limitedObs ? "Limited observations — clinical judgement required" : null,
      unavailable.length > 0
        ? `Not available: ${unavailable.join(", ")}`
        : null,
      `Calculated at: ${ts}`,
    ].filter(Boolean) as string[];

    const result: CalculatorResult = {
      medication: "NEWS2 Score",
      dosage: `Total ${totalScore} (${parametersScored}/${parametersTotal})`,
      quantity: risk?.label || "Insufficient data",
      duration: undefined,
      notes: notesLines.join("\n"),
      vitals,
    };

    onComplete([result]);
  };

  const ScorePill = ({ score }: { score: number }) =>
    score < 0 ? (
      <span
        className={`inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full text-xs font-semibold ${
          isDarkMode ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-500"
        }`}
      >
        —
      </span>
    ) : (
      <span
        className={`inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full text-xs font-semibold ${
          score === 0
            ? isDarkMode
              ? "bg-green-900/40 text-green-300"
              : "bg-green-100 text-green-800"
            : score === 1
              ? isDarkMode
                ? "bg-yellow-900/40 text-yellow-300"
                : "bg-yellow-100 text-yellow-800"
              : score === 2
                ? isDarkMode
                  ? "bg-orange-900/40 text-orange-300"
                  : "bg-orange-100 text-orange-800"
                : isDarkMode
                  ? "bg-red-900/40 text-red-300"
                  : "bg-red-100 text-red-800"
        }`}
      >
        {score}
      </span>
    );

  return (
    <div
      className={`relative space-y-6 ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
    >
      {isSaving && (
        <div
          className="absolute inset-0 flex items-center justify-center rounded-lg z-20"
          style={{
            background: isDarkMode
              ? "rgba(17,17,17,0.75)"
              : "rgba(255,255,255,0.8)",
          }}
        >
          <div
            className={`flex items-center gap-2 text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
          >
            <svg
              className="w-4 h-4 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            Saving NEWS2 vitals…
          </div>
        </div>
      )}
      <div>
        <h3 className="text-base font-semibold flex items-center gap-2">
          <Activity className="h-4 w-4" />
          NEWS2 — National Early Warning Score
        </h3>
        <p
          className={`text-xs mt-1 ${
            isDarkMode ? "text-gray-400" : "text-gray-600"
          }`}
        >
          Enter only what you can measure. Empty fields are excluded from the
          total. Score updates as you type.
        </p>
      </div>

      {/* Scale toggle */}
      <div className="space-y-2">
        <Label>SpO₂ scale</Label>
        <RadioGroup
          value={scale}
          onValueChange={(v) => setScale(v as Scale)}
          className="flex gap-4"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem id="news2-scale-1" value="1" />
            <Label
              htmlFor="news2-scale-1"
              className="font-normal cursor-pointer"
            >
              Scale 1 (default)
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem id="news2-scale-2" value="2" />
            <Label
              htmlFor="news2-scale-2"
              className="font-normal cursor-pointer"
            >
              Scale 2 (hypercapnic resp. failure)
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Parameters */}
      <div className="space-y-4">
        {/* Respiration */}
        <div className="flex items-end gap-3">
          <div className="flex-1 space-y-1">
            <Label htmlFor="news2-resp">Respiration rate (bpm)</Label>
            <Input
              id="news2-resp"
              type="number"
              inputMode="numeric"
              min={1}
              max={80}
              placeholder="e.g. 18"
              value={respiration}
              onChange={(e) => setRespiration(e.target.value)}
            />
          </div>
          <ScorePill score={respirationScore} />
          {/* <Button type="button" variant="ghost" size="sm" onClick={() => setRespiration("")}>N/A</Button> */}
        </div>

        {/* SpO2 */}
        <div className="flex items-end gap-3">
          <div className="flex-1 space-y-1">
            <Label htmlFor="news2-spo2">SpO₂ (%)</Label>
            <Input
              id="news2-spo2"
              type="number"
              inputMode="numeric"
              min={50}
              max={100}
              placeholder="e.g. 97"
              value={spo2}
              onChange={(e) => setSpo2(e.target.value)}
            />
          </div>
          <ScorePill score={spo2Score} />
          {/* <Button type="button" variant="ghost" size="sm" onClick={() => setSpo2("")}>N/A</Button> */}
        </div>

        {/* Supplemental oxygen — only meaningful when SpO2 is recorded */}
        <div className="flex items-end gap-3">
          <div className="flex-1 space-y-1">
            <Label>Supplemental oxygen</Label>
            <Select
              value={supplementalOxygen}
              onValueChange={(v) =>
                setSupplementalOxygen(v as "air" | "oxygen")
              }
              disabled={spo2 === ""}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="air">Air</SelectItem>
                <SelectItem value="oxygen">On supplemental O₂</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <ScorePill score={supplementalOxygenScore} />
          <div className="w-[44px]" />
        </div>

        {/* Systolic BP */}
        {/* Blood Pressure */}
        <div className="flex items-end gap-3">
          <div className="flex-1 space-y-1">
            <Label htmlFor="news2-bp">Blood Pressure — Systolic / Diastolic (mmHg)</Label>
            <InputMask
              mask="999/999"
              maskChar={null}
              value={bloodPressure}
              onChange={(e) => {
                const nextBloodPressure = e.target.value;
                setBloodPressure(nextBloodPressure);
                if (
                  bloodPressureError &&
                  (nextBloodPressure.trim() === "" ||
                    hasCompleteBloodPressure(nextBloodPressure))
                ) {
                  setBloodPressureError("");
                }
              }}
            >
              {(inputProps: any) => (
                <Input
                  {...inputProps}
                  id="news2-bp"
                  type="text"
                  inputMode="numeric"
                  placeholder="e.g. 120/80"
                  aria-invalid={bloodPressureError ? "true" : "false"}
                  aria-describedby={
                    bloodPressureError ? "news2-bp-error" : undefined
                  }
                  className={
                    bloodPressureError
                      ? "border-red-500 focus-visible:ring-red-500"
                      : undefined
                  }
                />
              )}
            </InputMask>
            {bloodPressureError && (
              <p id="news2-bp-error" className="text-xs text-red-500">
                {bloodPressureError}
              </p>
            )}
          </div>
          <ScorePill score={systolicScore} />
          {/* <Button type="button" variant="ghost" size="sm" onClick={() => setBloodPressure("")}>N/A</Button> */}
        </div>

        {/* Pulse */}
        <div className="flex items-end gap-3">
          <div className="flex-1 space-y-1">
            <Label htmlFor="news2-pulse">Pulse (bpm)</Label>
            <Input
              id="news2-pulse"
              type="number"
              inputMode="numeric"
              min={20}
              max={250}
              placeholder="e.g. 72"
              value={pulse}
              onChange={(e) => setPulse(e.target.value)}
            />
          </div>
          <ScorePill score={pulseScore} />
          {/* <Button type="button" variant="ghost" size="sm" onClick={() => setPulse("")}>N/A</Button> */}
        </div>

        {/* Consciousness (ACVPU) */}
        <div className="flex items-end gap-3">
          <div className="flex-1 space-y-1">
            <Label>Consciousness (ACVPU)</Label>
            <Select
              value={consciousness}
              onValueChange={(v) => setConsciousness(v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alert">Alert</SelectItem>
                <SelectItem value="confusion">New Confusion</SelectItem>
                <SelectItem value="voice">Voice</SelectItem>
                <SelectItem value="pain">Pain</SelectItem>
                <SelectItem value="unresponsive">Unresponsive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <ScorePill score={consciousnessScore} />
          {/* <Button type="button" variant="ghost" size="sm" onClick={() => setConsciousness("")}>N/A</Button> */}
        </div>

        {/* Temperature */}
        <div className="flex items-end gap-3">
          <div className="flex-1 space-y-1">
            <Label htmlFor="news2-temp">Temperature (°C)</Label>
            <Input
              id="news2-temp"
              type="number"
              step="0.1"
              inputMode="decimal"
              min={25}
              max={45}
              placeholder="e.g. 36.8"
              value={temperature}
              onChange={(e) => setTemperature(e.target.value)}
            />
          </div>
          <ScorePill score={temperatureScore} />
          {/* <Button type="button" variant="ghost" size="sm" onClick={() => setTemperature("")}>N/A</Button> */}
        </div>
      </div>

      {/* Total + risk band */}
      <Card className={isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white"}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Total NEWS2 score</span>
            <span className="text-2xl font-bold">{totalScore}</span>
          </CardTitle>
          <CardDescription
            className={isDarkMode ? "text-gray-400" : "text-gray-600"}
          >
            From {parametersScored} of {parametersTotal} parameters
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {risk && (
            <div className={`rounded-md border p-3 ${risk.cls}`}>
              <p className="text-sm font-semibold">{risk.label}</p>
              <p className="text-xs mt-1">{risk.description}</p>
            </div>
          )}
          {limitedObs && (
            <div
              className={`rounded-md border p-3 text-xs ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 text-gray-300"
                  : "bg-gray-50 border-gray-200 text-gray-700"
              }`}
            >
              <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />
              Limited observations — clinical judgement required. State which
              parameters are available when calling 999.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          className="flex-1"
          onClick={handleSave}
          disabled={parametersScored === 0}
        >
          Save score
        </Button>
      </div>
    </div>
  );
}
